/**
 * `.pix` 用のシンプルなコンテキスト補完。
 *  - 行頭 `@` → コマンド名
 *  - `@bgm `/`@bg `/`@chara `/`@se `/`@transition ` → サブコマンド候補
 *  - `id=` / `file=` / `name=` / `face=` → manifest から候補
 *  - `[ ` → インラインタグ候補
 */

import type { CompletionContext, CompletionResult } from "@codemirror/autocomplete";
import type { ProjectManifest } from "@pix/shared";

interface Opts {
  manifestRef: { current: ProjectManifest };
}

const COMMANDS = [
  { name: "bgm", info: "BGM 再生 / 停止" },
  { name: "bg", info: "背景表示 / 非表示" },
  { name: "chara", info: "立ち絵 show/hide/face/move" },
  { name: "se", info: "効果音" },
  { name: "wait", info: "待機 (ms=...)" },
  { name: "jump", info: "ラベルへジャンプ" },
  { name: "call", info: "サブルーチン呼び出し" },
  { name: "return", info: "呼び出し元へ戻る" },
  { name: "end", info: "終了" },
  { name: "set", info: "変数代入" },
  { name: "unset", info: "変数解除" },
  { name: "if", info: "条件分岐 (@endif で閉じる)" },
  { name: "elseif", info: "@if のサブ分岐" },
  { name: "else", info: "@if の else" },
  { name: "endif", info: "@if を閉じる" },
  { name: "for", info: "繰り返し (@endfor で閉じる)" },
  { name: "endfor", info: "@for を閉じる" },
  { name: "transition", info: "画面遷移エフェクト" },
  { name: "effect", info: "演出エフェクト" },
  { name: "screen", info: "画面効果" },
  { name: "face", info: "フェイスウィンドウ" },
];

const SUBS: Record<string, string[]> = {
  bgm: ["play", "stop"],
  bg: ["show", "hide"],
  chara: ["show", "hide", "face", "move", "hide_all"],
  se: ["play", "stop"],
  transition: ["fade", "wipe", "dissolve"],
  effect: ["shake", "flash"],
  screen: ["flash", "fade", "filter"],
};

const INLINE_TAGS = [
  "ruby",
  "wait",
  "br",
  "color",
  "size",
  "shake",
  "var",
  "speed",
];

export function pixCompletionSource(opts: Opts) {
  return (ctx: CompletionContext): CompletionResult | null => {
    const lineObj = ctx.state.doc.lineAt(ctx.pos);
    const before = lineObj.text.slice(0, ctx.pos - lineObj.from);

    // 行頭 @ → コマンド一覧
    const cmdMatch = before.match(/^@(\w*)$/);
    if (cmdMatch) {
      return {
        from: lineObj.from + 1, // @ の直後
        options: COMMANDS.map((c) => ({
          label: c.name,
          info: c.info,
          type: "keyword",
        })),
        validFor: /^\w*$/,
      };
    }

    // @cmd <sub> サブコマンド
    const subMatch = before.match(/^@(\w+)\s+(\w*)$/);
    if (subMatch) {
      const cmd = subMatch[1]!;
      const subs = SUBS[cmd];
      if (subs) {
        return {
          from: ctx.pos - (subMatch[2]?.length ?? 0),
          options: subs.map((s) => ({ label: s, type: "function" })),
          validFor: /^\w*$/,
        };
      }
    }

    // id= 補完(キャラ ID)
    const idMatch = before.match(/\bid=(\w*)$/);
    if (idMatch && /^@chara\b/.test(before)) {
      const ids = Object.keys(opts.manifestRef.current.characters ?? {});
      return {
        from: ctx.pos - (idMatch[1]?.length ?? 0),
        options: ids.map((id) => ({
          label: id,
          info: opts.manifestRef.current.characters?.[id]?.name,
          type: "variable",
        })),
        validFor: /^[\w-]*$/,
      };
    }

    // face= / expression= 補完
    const faceMatch = before.match(/\b(?:face|expression)=(\w*)$/);
    if (faceMatch && /^@chara\b/.test(before)) {
      // 直近の id=X を拾って expressions を取り出す
      const m = before.match(/\bid=([\w-]+)/);
      if (m) {
        const cdef = opts.manifestRef.current.characters?.[m[1]!];
        if (cdef?.expressions) {
          return {
            from: ctx.pos - (faceMatch[1]?.length ?? 0),
            options: cdef.expressions.map((e) => ({
              label: e,
              type: "enum",
            })),
            validFor: /^[\w-]*$/,
          };
        }
      }
    }

    // file= 補完: 文脈で bgm / se を判定
    const fileMatch = before.match(/\bfile=(\w*)$/);
    if (fileMatch) {
      let pool: Record<string, unknown> = {};
      if (/^@bgm\b/.test(before)) {
        pool = opts.manifestRef.current.audio?.bgm ?? {};
      } else if (/^@se\b/.test(before)) {
        pool = opts.manifestRef.current.audio?.se ?? {};
      }
      const ids = Object.keys(pool);
      if (ids.length > 0) {
        return {
          from: ctx.pos - (fileMatch[1]?.length ?? 0),
          options: ids.map((id) => ({ label: id, type: "variable" })),
          validFor: /^[\w-]*$/,
        };
      }
    }

    // name= 補完(背景)
    const nameMatch = before.match(/\bname=(\w*)$/);
    if (nameMatch && /^@bg\b/.test(before)) {
      const ids = Object.keys(opts.manifestRef.current.backgrounds ?? {});
      return {
        from: ctx.pos - (nameMatch[1]?.length ?? 0),
        options: ids.map((id) => ({ label: id, type: "variable" })),
        validFor: /^[\w-]*$/,
      };
    }

    // pos= 補完(立ち絵位置)
    const posMatch = before.match(/\bpos=(\w*)$/);
    if (posMatch && /^@chara\b/.test(before)) {
      return {
        from: ctx.pos - (posMatch[1]?.length ?? 0),
        options: ["left-edge", "left", "center", "right", "right-edge"].map((p) => ({
          label: p,
          type: "enum",
        })),
        validFor: /^[\w-]*$/,
      };
    }

    // インラインタグ [
    const tagMatch = before.match(/\[(\w*)$/);
    if (tagMatch) {
      return {
        from: ctx.pos - (tagMatch[1]?.length ?? 0),
        options: INLINE_TAGS.map((t) => ({ label: t, type: "keyword" })),
        validFor: /^\w*$/,
      };
    }

    return null;
  };
}
